import { Grid2 as Grid } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { GridProps } from '@portals/core/src/components/Grid/Grid';
import { ApiClientProvider } from '@portals/sip-client-data/src/general/ApiClient';
import { useFormikContext } from 'formik';
import { getFixedT } from 'i18next';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Criteria } from '../../../../../config';

type Props = GridProps & {
  criteria: Criteria | null | boolean;
  handleCancel: (e) => void;
  close: (goToMap?: boolean) => void;
  handleSubmitFromProps?: (e) => void;
  showResultFilterDrawer?: (showAllFilter?: boolean, criteria?: Criteria) => void;
};

const t = getFixedT(null, 'core-immobilien');

export const FilterDrawerActions: React.FC<Props> = ({ handleCancel, close, handleSubmitFromProps, sx }) => {
  const { isValid, values, errors, submitForm } = useFormikContext();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isValid) {
      setCount(0);
    }
  }, [isValid]);

  const fetchEstateCount = (values, errors) => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);
    ApiClientProvider.getApiClient()
      .getEstateCount(values)
      .then((response) => {
        setCount(response.totalItems);
      })
      .catch(() => {
        setCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const debounceFetch = useRef(debounce((values, errors) => fetchEstateCount(values, errors), 300)).current;

  useEffect(() => {
    debounceFetch(values, errors);
  }, [values, errors, debounceFetch]);

  const handleSubmit = useCallback(() => {
    submitForm();
    close(true);
  }, [close, submitForm]);

  const getClickHandler = handleSubmitFromProps !== undefined ? handleSubmitFromProps : handleSubmit;

  const disabled = !isValid || !values['zipCityEstateId'] || values['zipCityEstateId'].trim() === '';

  return (
    <Grid container spacing={5} sx={sx}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Button color="secondary" onClick={handleCancel} fullWidth>
          {t('cancel')}
        </Button>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Button loading={loading && !disabled} color="primary" onClick={getClickHandler} fullWidth disabled={disabled}>
          {t('searchResultFilter.showResults', { count: count })}
        </Button>
      </Grid>
    </Grid>
  );
};

FilterDrawerActions.displayName = 'FilterDrawerActions';
